import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { longPressLocationChanged } from '../../../../common/store/item-edit';
import { useGlMap } from '../../../contexts/context';
import { INITIAL_GEOLOCATION_MAP_ZOOM } from '../../../../common/utils/constants/constants';
import {} from '../../../utils/helpers/utils';
import {
  OMIT_POLYGON_TYPES,
  createOmitPolygons,
} from '../../../utils/helpers/omitPolygonsUtils';
import { useMapBoundsPolygon } from '../../../utils/hooks/useMapBoundsPolygon';
import { useMapWaterPolygon } from '../../../contexts/mapWaterPolygonContext';

const LONG_PRESS_DURATION_MS = 400;

const useCreateCorrectCoordinatesFn = (glMap: mapboxgl.Map | null) => {
  const mapWaterPolygon = useMapWaterPolygon();
  const boundsPolygon = useMapBoundsPolygon(glMap);
  return useMemo(() => {
    if (!mapWaterPolygon || !boundsPolygon) {
      return () => () => null;
    }

    return () => {
      return createOmitPolygons([
        {
          type: OMIT_POLYGON_TYPES.allow,
          geometry: boundsPolygon,
        },
        {
          type: OMIT_POLYGON_TYPES.restrict,
          geometry: mapWaterPolygon,
        },
      ]);
    };
  }, [mapWaterPolygon, boundsPolygon]);
};

const LongPressAdditionControl = () => {
  const glMap = useGlMap();
  const dispatch = useDispatch();

  const setLongPressLocation = useCallback(
    (location) => dispatch(longPressLocationChanged(location)),
    [dispatch],
  );

  const createCorrectCoordinatesFn = useCreateCorrectCoordinatesFn(glMap);

  useEffect(() => {
    if (!glMap) {
      return;
    }

    const handleLongPress = (e) => {
      if (glMap.getZoom() < INITIAL_GEOLOCATION_MAP_ZOOM) {
        return;
      }

      const { clientX, clientY } = e.detail;
      const correctCoordinates = createCorrectCoordinatesFn();

      const modifiedLocation = correctCoordinates(
        glMap.unproject([clientX, clientY]).toArray(),
      );

      if (modifiedLocation) {
        setLongPressLocation(modifiedLocation);
      }
    };

    const container = glMap.getContainer();
    container.setAttribute(
      'data-long-press-delay',
      String(LONG_PRESS_DURATION_MS),
    );

    const canvas = glMap.getCanvas();
    canvas.addEventListener('long-press', handleLongPress);

    return () => {
      canvas.removeEventListener('long-press', handleLongPress);
      container.removeAttribute('data-long-press-delay');
    };
  }, [glMap, setLongPressLocation, createCorrectCoordinatesFn]);

  return null;
};

export default LongPressAdditionControl;
